import { StorageService } from '@/app/core/auth/storage.service'
import { ApiResponse } from '@/app/shared/models/api-response.model'
import {
  ExtendedConversationDTO,
  ExtendedMessageDTO,
  IConversation,
  IMessage,
} from '@/app/shared/models/chat/chat.model'
import { PatientInfo } from '@/app/shared/models/patient-info/patient-info.model'
import { PatientInfoApiService } from '@/app/shared/services/api/patient-info/patient-info.api.service'
import { MediaService } from '@/app/shared/services/media.service'
import { computed, inject } from '@angular/core'
import { tapResponse } from '@ngrx/operators'
import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals'
import { rxMethod } from '@ngrx/signals/rxjs-interop'
import {
  debounceTime,
  distinctUntilChanged,
  of,
  pipe,
  switchMap,
  tap,
} from 'rxjs'
import { ChatService, HydraCollection } from '../services/chat.service'
import { MercureService } from '../services/mercure.service'
import { ChatMessageUtils } from '../utils/chat-message.utils'

interface MercureEventData {
  status?: string
  id?: string | number
  messageId?: string | number
  docOpened?: boolean
  read?: boolean
  from?: string | number
}

interface ChatState {
  // Normalized state for stability
  conversationEntities: Record<number, IConversation> // Data
  conversationIds: number[] // Order (strictly controlled)

  messages: IMessage[]
  selectedConversationId: number | null
  isConversationsLoading: boolean
  isMessagesLoading: boolean
  isSending: boolean
  isTyping: boolean
  currentPage: number
  hasMoreMessages: boolean
  isLoadingMore: boolean
  messageCache: Record<number, IMessage[]>
  isUploading: boolean
  uploadedMediaIri: string | null
  uploadedMediaName: string | null
  uploadedMediaType: string | null
  uploadedMediaBlobUrl: string | null
  isDragging: boolean
  patientSearchResults: IConversation[]
  isSearchingPatients: boolean
}

const initialState: ChatState = {
  conversationEntities: {},
  conversationIds: [],
  messages: [],
  selectedConversationId: null,
  isConversationsLoading: false,
  isMessagesLoading: false,
  isSending: false,
  isTyping: false,
  currentPage: 1,
  hasMoreMessages: false,
  isLoadingMore: false,
  messageCache: {},
  isUploading: false,
  uploadedMediaIri: null,
  uploadedMediaName: null,
  uploadedMediaType: null,
  uploadedMediaBlobUrl: null,
  isDragging: false,
  patientSearchResults: [],
  isSearchingPatients: false,
}

export const ChatStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed(
    ({ conversationEntities, conversationIds, selectedConversationId }) => ({
      totalUnreadCount: computed(() => {
        const entities = conversationEntities()
        return Object.values(entities).reduce(
          (acc, conv) => acc + (conv.unread || 0),
          0,
        )
      }),
      selectedConversation: computed(() => {
        const entities = conversationEntities()
        const id = selectedConversationId()
        return id !== null ? entities[id] : undefined
      }),

      // THE STABLE LIST used by the UI
      sortedConversations: computed(() => {
        const entities = conversationEntities()
        const ids = conversationIds()
        return ids.map((id) => entities[id]).filter(Boolean)
      }),

      // Legacy getter if needed elsewhere (or we can remove it)
      conversations: computed(() => {
        const entities = conversationEntities()
        const ids = conversationIds()
        return ids.map((id) => entities[id]).filter(Boolean)
      }),
    }),
  ),
  withMethods(
    (
      store,
      chatService = inject(ChatService),
      mercureService = inject(MercureService),
      storageService = inject(StorageService),
      mediaService = inject(MediaService),
      patientApiService = inject(PatientInfoApiService),
    ) => {
      const updateConversation = (
        idOrConv: number | IConversation,
        partial?: Partial<IConversation>,
      ) => {
        const id = typeof idOrConv === 'number' ? idOrConv : idOrConv.id
        const changes = typeof idOrConv === 'number' ? partial! : idOrConv

        patchState(store, (state) => {
          const entity = state.conversationEntities[id]
          if (!entity) return {}
          return {
            conversationEntities: {
              ...state.conversationEntities,
              [id]: { ...entity, ...changes },
            },
          }
        })
      }

      const addConversation = (conversation: IConversation) => {
        patchState(store, (state) => {
          const exists = !!state.conversationEntities[conversation.id]

          // Update Entity
          const newEntities = {
            ...state.conversationEntities,
            [conversation.id]: conversation,
          }

          // Update IDs (Move to top if new)
          let newIds = state.conversationIds
          if (!exists) {
            newIds = [conversation.id, ...state.conversationIds]
          } else {
            newIds = [
              conversation.id,
              ...state.conversationIds.filter((id) => id !== conversation.id),
            ]
          }

          return {
            conversationEntities: newEntities,
            conversationIds: newIds,
          }
        })
      }

      const setConversationTyping = (convId: number, isTyping: boolean) => {
        const conv = store.conversations().find((c) => c.id === convId)
        if (!conv) return

        if (conv.typingTimeout) {
          clearTimeout(conv.typingTimeout)
        }

        let timeout = null
        if (isTyping) {
          timeout = setTimeout(() => {
            updateConversation(convId, { isTyping: false, typingTimeout: null })
            if (store.selectedConversationId() === convId) {
              patchState(store, { isTyping: false })
            }
          }, 3000)
        }

        updateConversation(convId, { isTyping, typingTimeout: timeout })

        if (store.selectedConversationId() === convId) {
          patchState(store, { isTyping })
        }
      }

      const hydrateLastMessage = (
        convId: number,
        messageIri: string | object,
      ) => {
        chatService.getMessage(messageIri).subscribe({
          next: (msg: ExtendedMessageDTO) => {
            updateConversation(convId, {
              lastMessage: msg.content || '',
              timestamp: msg.createdAt ? new Date(msg.createdAt) : new Date(),
            })
          },
          error: (err: unknown) =>
            console.error(`[Store] Msg Hydration error:`, err),
        })
      }

      const mapMessages = (
        dtos: ExtendedMessageDTO[],
        userId: number,
      ): IMessage[] => {
        return dtos.map((msg) => {
          const senderIri = `/api/users/${userId}`
          const isMe = msg.sender === senderIri

          return {
            id: msg.id!,
            text: msg.content || '',
            sender: (isMe ? 'me' : 'other') as 'me' | 'other',
            timestamp: msg.createdAt ? new Date(msg.createdAt) : new Date(),
            senderName: msg.createdByName || undefined,
            senderId: msg.sender,
            media: msg.media || undefined,
            docOpened: msg.docOpened || false,
            read: msg.read || false,
          }
        })
      }

      const hydrateMedia = (messageId: number, mediaIri: string) => {
        mediaService.getMedia(mediaIri).subscribe({
          next: (media) => {
            const fileName =
              media.filepath.split(/[/\\]/).pop() || 'Pièce jointe'

            let mediaType = media.type
            if (!mediaType) {
              const ext = fileName.split('.').pop()?.toLowerCase()
              if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext || ''))
                mediaType = 'image/' + ext
              else if (['mp4', 'webm', 'ogg', 'mov'].includes(ext || ''))
                mediaType = 'video/' + ext
              else if (['mp3', 'wav', 'mpeg'].includes(ext || ''))
                mediaType = 'audio/' + ext
              else if (ext === 'pdf') mediaType = 'application/pdf'
              else mediaType = 'application/octet-stream'
            }

            const isAudio =
              mediaType?.startsWith('audio/') ||
              fileName.toLowerCase().endsWith('.mp3') ||
              fileName.toLowerCase().endsWith('.wav')

            patchState(store, (state) => {
              const updatedMessages = state.messages.map((m) =>
                m.id === messageId
                  ? {
                      ...m,
                      mediaType: mediaType,
                      mediaName: fileName,
                      mediaFilePath: media.filepath,
                    }
                  : m,
              )

              // Also update cache for this conversation
              const convId = state.selectedConversationId
              const updatedCache = { ...state.messageCache }
              if (convId) {
                updatedCache[convId] = (updatedCache[convId] || []).map((m) =>
                  m.id === messageId
                    ? {
                        ...m,
                        mediaType: mediaType,
                        mediaName: fileName,
                        mediaFilePath: media.filepath,
                      }
                    : m,
                )
              }

              return {
                messages: updatedMessages,
                messageCache: updatedCache,
              }
            })

            // Auto-fetch blob for audio so it's ready to play
            if (isAudio && media.filepath) {
              mediaService.getBlobUrl(media.filepath).subscribe((url) => {
                if (url) {
                  patchState(store, (state) => {
                    const updateMsg = (m: IMessage) =>
                      m.id === messageId ? { ...m, mediaBlobUrl: url } : m

                    const updatedMessages = state.messages.map(updateMsg)

                    // Update cache
                    const cId = state.selectedConversationId
                    const updatedCache = { ...state.messageCache }
                    if (cId && updatedCache[cId]) {
                      updatedCache[cId] = updatedCache[cId].map(updateMsg)
                    }

                    return {
                      messages: updatedMessages,
                      messageCache: updatedCache,
                    }
                  })
                }
              })
            }
          },
          error: (err) => {
            console.error(`[Store] Media hydration error:`, err)
            // Settling with a fallback to avoid infinite loading
            patchState(store, (state) => ({
              messages: state.messages.map((m) =>
                m.id === messageId ? { ...m, mediaType: 'unknown' } : m,
              ),
            }))
          },
        })
      }

      const markAsRead = (convId: number) => {
        // Optimistically update conversation unread count
        patchState(store, (state) => {
          const entity = state.conversationEntities[convId]
          // Skip if already 0 (strict check)
          if (!entity || entity.unread === 0) return {}

          return {
            conversationEntities: {
              ...state.conversationEntities,
              [convId]: { ...entity, unread: 0 },
            },
          }
        })

        // Identify unread messages from 'other' to determine the last one
        const unreadMessages = store
          .messages()
          .filter((m) => !m.read && m.sender === 'other')

        const lastUnread =
          unreadMessages.length > 0
            ? unreadMessages[unreadMessages.length - 1]
            : null

        // Use the global mark-as-read endpoint
        chatService.markAsRead(convId).subscribe({
          next: () => {
            // Update local messages state
            if (unreadMessages.length > 0) {
              patchState(store, (state) => ({
                messages: state.messages.map((m) =>
                  !m.read && m.sender === 'other' ? { ...m, read: true } : m,
                ),
              }))
            }

            // Only publish Mercure event for the LAST unread message
            if (lastUnread) {
              const topics = [`/conversations/${convId}`]
              const senderId = lastUnread.senderId?.split('/').pop()
              if (senderId) {
                topics.push(`/users/${senderId}/conversations`)
              }

              // Manual publish to topics
              mercureService
                .publish(
                  topics,
                  {
                    status: 'read',
                    id: convId,
                    messageId: lastUnread.id,
                    read: true,
                  },
                  false,
                  'read',
                )
                .subscribe({
                  error: (e) =>
                    console.error(
                      '[Store] Manual publish read event failed',
                      e,
                    ),
                })
            }
          },
          error: (err) =>
            console.error('[Store] Global markAsRead failed', err),
        })
      }

      const loadMessages = rxMethod<{
        convId: number
        userId: number
      }>(
        pipe(
          tap(({ convId }) => {
            const cache = store.messageCache()[convId]
            if (cache) {
              patchState(store, { messages: cache, isMessagesLoading: false })
              // Still trigger hydration for media if missing
              cache.forEach((m) => {
                if (m.media && !m.mediaType) {
                  hydrateMedia(m.id, m.media)
                }
              })
              // Check for unread messages in cache too
              if (
                store.selectedConversationId() !== null &&
                convId === store.selectedConversationId()
              ) {
                setTimeout(() => markAsRead(convId), 100)
              }
            } else {
              patchState(store, { isMessagesLoading: true, messages: [] })
            }
          }),
          switchMap(({ convId, userId }) => {
            return chatService.getMessages(convId, 1).pipe(
              tapResponse({
                next: (res: HydraCollection<ExtendedMessageDTO>) => {
                  const dtos = res.member || res['hydra:member'] || []
                  // API returns DESC, we want chronological ASC in UI
                  const newMessages = ChatMessageUtils.sortMessages(
                    mapMessages(dtos, userId),
                  )

                  // Trigger hydration for messages with media
                  newMessages.forEach((m) => {
                    if (m.media && !m.mediaBlobUrl) {
                      hydrateMedia(m.id, m.media)
                    }
                  })

                  const hasMore = !!(
                    res.view?.next ||
                    res['hydra:view']?.['hydra:next'] ||
                    res['hydra:view']?.next
                  )

                  patchState(store, (state) => ({
                    messages: newMessages,
                    messageCache: {
                      ...state.messageCache,
                      [convId]: newMessages,
                    },
                    isMessagesLoading: false,
                    hasMoreMessages: hasMore,
                    currentPage: 1,
                  }))

                  // Mark as read after loading, if this represents the active conversation
                  if (
                    store.selectedConversationId() !== null &&
                    convId === store.selectedConversationId()
                  ) {
                    markAsRead(convId)
                  }
                },
                error: (err: unknown) => {
                  console.error(err)
                  patchState(store, { isMessagesLoading: false })
                },
              }),
            )
          }),
        ),
      )

      const loadConversations = rxMethod<{ userId: number }>(
        pipe(
          tap(() => {
            if (store.conversationIds().length === 0) {
              patchState(store, { isConversationsLoading: true })
            }
          }),
          switchMap(({ userId }) =>
            chatService.getConversations(userId).pipe(
              tapResponse({
                next: (dtos: ExtendedConversationDTO[]) => {
                  const mappedConvs = dtos.map((dto) =>
                    chatService.mapConversation(dto, userId),
                  )

                  patchState(store, (state) => {
                    // 1. Update Entities (Merge with existing)
                    const newEntities = { ...state.conversationEntities }
                    mappedConvs.forEach((conv) => {
                      const existing = newEntities[conv.id]
                      if (existing) {
                        newEntities[conv.id] = {
                          ...conv,
                          name:
                            conv.name === 'Inconnu' &&
                            existing.name !== 'Inconnu'
                              ? existing.name
                              : conv.name,
                          lastMessage:
                            conv.lastMessage === 'Chargement...' &&
                            existing.lastMessage !== 'Chargement...'
                              ? existing.lastMessage
                              : conv.lastMessage,
                          phone: conv.phone || existing.phone,
                          avatar: conv.avatar || existing.avatar,
                          timestamp:
                            conv.lastMessage === 'Chargement...' &&
                            existing.lastMessage !== 'Chargement...'
                              ? existing.timestamp
                              : existing.timestamp &&
                                  conv.timestamp?.getTime() ===
                                    existing.timestamp?.getTime()
                                ? existing.timestamp
                                : conv.timestamp,
                        }
                      } else {
                        newEntities[conv.id] = conv
                      }
                    })

                    const allConvs = Object.values(newEntities)
                    const sortedIds = allConvs
                      .sort((a, b) => {
                        const timeA = a.timestamp
                          ? new Date(a.timestamp).getTime()
                          : 0
                        const timeB = b.timestamp
                          ? new Date(b.timestamp).getTime()
                          : 0
                        if (timeA !== timeB) return timeB - timeA
                        return b.id - a.id
                      })
                      .map((c) => c.id)

                    return {
                      conversationEntities: newEntities,
                      conversationIds: sortedIds,
                      isConversationsLoading: false,
                    }
                  })

                  dtos.forEach((dto) => {
                    const id = dto.id
                    if (typeof id !== 'number') return

                    const msgs = dto.messages
                    if (msgs && msgs.length > 0) {
                      const lastMsg = msgs[msgs.length - 1]
                      if (lastMsg && typeof lastMsg === 'string') {
                        hydrateLastMessage(id, lastMsg)
                      }
                    }
                  })
                },
                error: (err: unknown) => {
                  console.error(err)
                  patchState(store, { isConversationsLoading: false })
                },
              }),
            ),
          ),
        ),
      )

      return {
        setLoading(isLoading: boolean) {
          patchState(store, { isConversationsLoading: isLoading })
        },

        hydrateMedia,

        // setConversations removed (incompatible)

        addConversation,
        updateConversation,
        loadConversations,
        loadMessages,
        markAsRead,

        loadMoreMessages(userId: number) {
          const convId = store.selectedConversationId()
          if (
            convId === null ||
            store.isLoadingMore() ||
            !store.hasMoreMessages() ||
            store.isMessagesLoading()
          ) {
            return
          }

          const nextPage = store.currentPage() + 1
          patchState(store, { isLoadingMore: true })

          chatService
            .getMessages(convId, nextPage)
            .pipe(tap())
            .subscribe({
              next: (res: HydraCollection<ExtendedMessageDTO>) => {
                const dtos = res.member || res['hydra:member'] || []
                const olderMessages = mapMessages(dtos, userId)
                const hasMore = !!(
                  res.view?.next ||
                  res['hydra:view']?.['hydra:next'] ||
                  res['hydra:view']?.next
                )

                patchState(store, (state) => {
                  const updatedMessages = ChatMessageUtils.sortMessages([
                    ...olderMessages,
                    ...state.messages,
                  ])
                  return {
                    messages: updatedMessages,
                    messageCache: {
                      ...state.messageCache,
                      [convId]: updatedMessages,
                    },
                    currentPage: nextPage,
                    hasMoreMessages: hasMore,
                    isLoadingMore: false,
                  }
                })
              },
              error: (err: unknown) => {
                console.error('[Store] Load more error:', err)
                patchState(store, { isLoadingMore: false })
              },
            })
        },

        replaceConversationId(oldId: number, newId: number) {
          patchState(store, (state) => {
            const entity = state.conversationEntities[oldId]
            if (!entity) return {}

            const newEntities = { ...state.conversationEntities }
            delete newEntities[oldId]
            newEntities[newId] = { ...entity, id: newId }

            const newIds = state.conversationIds.map((id) =>
              id === oldId ? newId : id,
            )

            const newCache = { ...state.messageCache }
            if (newCache[oldId]) {
              newCache[newId] = newCache[oldId]
              delete newCache[oldId]
            }

            const currentSelected = state.selectedConversationId
            const newSelected =
              currentSelected === oldId ? newId : currentSelected

            return {
              conversationEntities: newEntities,
              conversationIds: newIds,
              messageCache: newCache,
              selectedConversationId: newSelected,
            }
          })
        },

        setSelectedConversationId(id: number | null) {
          patchState(store, { selectedConversationId: id, isTyping: false })
        },

        setTyping(isTyping: boolean) {
          patchState(store, { isTyping })
        },

        setSending(isSending: boolean) {
          patchState(store, { isSending })
        },

        setUploading(isUploading: boolean) {
          patchState(store, { isUploading })
        },

        setUploadedMedia(
          iri: string | null,
          name: string | null,
          type: string | null,
          blobUrl: string | null = null,
        ) {
          patchState(store, {
            uploadedMediaIri: iri,
            uploadedMediaName: name,
            uploadedMediaType: type,
            uploadedMediaBlobUrl: blobUrl,
          })
        },

        setIsDragging(value: boolean) {
          patchState(store, { isDragging: value })
        },

        markMessageAsOpened(messageId: number) {
          chatService.patchMessage(messageId, { docOpened: true }).subscribe({
            next: () => {
              const convId = store.selectedConversationId()

              // Find the message to get senderId
              const message = store.messages().find((m) => m.id === messageId)

              if (convId !== null) {
                const topics = [`/conversations/${convId}`]
                const senderId = message?.senderId?.split('/').pop()
                if (senderId) {
                  topics.push(`/users/${senderId}/conversations`)
                }

                mercureService
                  .publish(
                    topics,
                    {
                      status: 'doc_opened',
                      id: convId,
                      messageId: messageId,
                      docOpened: true,
                    },
                    false,
                    'doc_opened',
                  )
                  .subscribe({
                    next: () =>
                      console.log('[Store] Manual publish successful'),
                    error: (e) =>
                      console.error('[Store] Manual publish failed', e),
                  })
              }

              patchState(store, (state) => {
                const updatedMessages = state.messages.map((m) =>
                  m.id === messageId ? { ...m, docOpened: true } : m,
                )

                // Update cache
                const updatedCache = { ...state.messageCache }
                if (convId && updatedCache[convId]) {
                  updatedCache[convId] = updatedCache[convId].map((m) =>
                    m.id === messageId ? { ...m, docOpened: true } : m,
                  )
                }

                return {
                  messages: updatedMessages,
                  messageCache: updatedCache,
                }
              })
            },
            error: (err) => {
              console.error('[Store] Error marking message as opened:', err)
            },
          })
        },

        updateMessageBlobUrl(messageId: number, blobUrl: string) {
          patchState(store, (state) => {
            const updatedMessages = state.messages.map((m) =>
              m.id === messageId ? { ...m, mediaBlobUrl: blobUrl } : m,
            )

            // Update cache
            const convId = state.selectedConversationId
            const updatedCache = { ...state.messageCache }
            if (convId !== null && updatedCache[convId]) {
              updatedCache[convId] = updatedCache[convId].map((m) =>
                m.id === messageId ? { ...m, mediaBlobUrl: blobUrl } : m,
              )
            }

            return {
              messages: updatedMessages,
              messageCache: updatedCache,
            }
          })
        },

        clearUploadedMedia() {
          patchState(store, {
            uploadedMediaIri: null,
            uploadedMediaName: null,
            uploadedMediaType: null,
            uploadedMediaBlobUrl: null,
          })
        },

        addMessage(message: IMessage) {
          patchState(store, (state) => {
            const selectedId = state.selectedConversationId
            const newMessages =
              selectedId === null ||
              state.messages.some((m) => m.id === message.id)
                ? state.messages
                : ChatMessageUtils.sortMessages([...state.messages, message])

            const updatedCache = { ...state.messageCache }
            if (selectedId !== null) {
              updatedCache[selectedId] = [
                ...(updatedCache[selectedId] || []),
                message,
              ]
            }

            // Update Entity (Last Message)
            const newEntities = { ...state.conversationEntities }
            if (selectedId !== null && newEntities[selectedId]) {
              newEntities[selectedId] = {
                ...newEntities[selectedId],
                lastMessage: message.text,
                timestamp: message.timestamp,
              }
            }

            // Update Order: Move active conversation to top if message added
            let newIds = state.conversationIds
            if (selectedId !== null && newEntities[selectedId]) {
              newIds = [
                selectedId,
                ...state.conversationIds.filter((id) => id !== selectedId),
              ]
            }

            return {
              messages: newMessages,
              messageCache: updatedCache,
              conversationEntities: newEntities,
              conversationIds: newIds,
            }
          })
        },

        updateMessageStatus(
          tempId: string,
          status: 'sent' | 'error',
          realId?: number,
          read?: boolean,
        ) {
          patchState(store, (state) => {
            // Check if successful send and realId already exists (e.g. via Mercure)
            if (status === 'sent' && realId) {
              const exists = state.messages.find(
                (m) => m.id === realId && m.tempId !== tempId,
              )
              if (exists) {
                // Duplicate detected!
                // We should remove the optimistic message (the one with tempId)
                // because the real one is already there.
                const filteredMessages = state.messages.filter(
                  (m) => m.tempId !== tempId,
                )

                const selectedId = state.selectedConversationId
                const updatedCache = { ...state.messageCache }

                if (selectedId !== null && updatedCache[selectedId]) {
                  updatedCache[selectedId] = updatedCache[selectedId].filter(
                    (m) => m.tempId !== tempId,
                  )
                }

                // We might need to update the conversation lastMessage too if it was relying on the temp one
                // But usually the real message update handles that.
                // Let's just ensure we don't break the conversation view.

                return {
                  messages: filteredMessages,
                  messageCache: updatedCache,
                }
              }
            }

            const updatedMessages = state.messages.map((msg) => {
              if (msg.tempId === tempId) {
                return {
                  ...msg,
                  status,
                  id: realId ?? msg.id,
                  read: read !== undefined ? read : msg.read,
                  tempId: status === 'sent' ? undefined : tempId,
                }
              }
              return msg
            })

            const selectedId = state.selectedConversationId
            const updatedCache = { ...state.messageCache }
            if (selectedId !== null) {
              updatedCache[selectedId] = updatedMessages
            }

            // Update Entity (Last Message)
            const newEntities = { ...state.conversationEntities }
            if (selectedId !== null && newEntities[selectedId]) {
              const lastMsg = updatedMessages[updatedMessages.length - 1]
              newEntities[selectedId] = {
                ...newEntities[selectedId],
                lastMessage:
                  lastMsg?.text || newEntities[selectedId].lastMessage,
                timestamp:
                  lastMsg?.timestamp || newEntities[selectedId].timestamp,
              }
            }

            return {
              messages: updatedMessages,
              messageCache: updatedCache,
              conversationEntities: newEntities,
            }
          })
        },

        silentlyRefreshMessages(convId: number, userId: number) {
          chatService.getMessages(convId).subscribe({
            next: (res) => {
              const dtos = res.member || res['hydra:member'] || []
              const fetchedMessages = mapMessages(dtos, userId)

              patchState(store, (state) => {
                // 1. Identify new messages
                const existingIds = new Set(state.messages.map((m) => m.id))
                const newMessages = fetchedMessages.filter(
                  (m) => !existingIds.has(m.id),
                )

                // 2. Update existing messages if their state (docOpened or read) has changed
                const updatedExistingMessages = state.messages.map(
                  (existing) => {
                    const fetched = fetchedMessages.find(
                      (f) => f.id === existing.id,
                    )
                    if (
                      fetched &&
                      (fetched.docOpened !== existing.docOpened ||
                        fetched.read !== existing.read)
                    ) {
                      const updated = {
                        ...existing,
                        docOpened: fetched.docOpened,
                        read: fetched.read,
                      }

                      // If docOpened is true, clear media properties to force 'archived' UI
                      if (fetched.docOpened) {
                        updated.media = undefined
                        updated.mediaBlobUrl = undefined
                        updated.mediaFilePath = undefined
                      }

                      return updated
                    }
                    return existing
                  },
                )

                const updatedMessages = ChatMessageUtils.sortMessages([
                  ...updatedExistingMessages,
                  ...newMessages,
                ])

                newMessages.forEach((m) => {
                  if (m.media && !m.mediaBlobUrl) {
                    hydrateMedia(m.id, m.media)
                  }
                })

                if (
                  newMessages.length > 0 ||
                  fetchedMessages.some((f) => {
                    const m = state.messages.find((ex) => ex.id === f.id)
                    return (
                      m && (m.docOpened !== f.docOpened || m.read !== f.read)
                    )
                  })
                ) {
                  // Update cache
                  const updatedCache = { ...state.messageCache }
                  updatedCache[convId] = updatedMessages

                  // Update conversations list preview (Entity update)
                  const lastMsg = updatedMessages[updatedMessages.length - 1]
                  const newEntities = { ...state.conversationEntities }

                  if (newEntities[convId]) {
                    newEntities[convId] = {
                      ...newEntities[convId],
                      lastMessage:
                        lastMsg?.text || newEntities[convId].lastMessage,
                      timestamp:
                        lastMsg?.timestamp || newEntities[convId].timestamp,
                    }
                  }

                  // Update Order if new messages (Move to top)
                  let newIds = state.conversationIds
                  if (newMessages.length > 0) {
                    newIds = [
                      convId,
                      ...state.conversationIds.filter((id) => id !== convId),
                    ]
                  }

                  return {
                    messages: updatedMessages,
                    messageCache: updatedCache,
                    conversationEntities: newEntities,
                    conversationIds: newIds,
                  }
                }
                return state
              })

              // If this is the active conversation, mark new messages as read immediately
              if (store.selectedConversationId() === convId) {
                markAsRead(convId)
              }
            },
            error: (err) => console.error('[Store] Silent refresh failed', err),
          })
        },

        searchPatients: rxMethod<string>(
          pipe(
            debounceTime(300),
            distinctUntilChanged(),
            tap((query) => {
              if (!query.trim()) {
                patchState(store, {
                  patientSearchResults: [],
                  isSearchingPatients: false,
                })
                return
              }
              patchState(store, { isSearchingPatients: true })
            }),
            switchMap((query) => {
              if (!query.trim())
                return of({
                  member: [],
                  totalItems: 0,
                  '@context': '',
                  '@id': '',
                  '@type': '',
                } as unknown as ApiResponse<PatientInfo>)
              return patientApiService.getAll({ search: query })
            }),
            tapResponse({
              next: (response: ApiResponse<PatientInfo>) => {
                const patients = response.member || []
                const mapped = patients.map((p: PatientInfo) =>
                  chatService.mapPatientToConversation(p),
                )
                patchState(store, {
                  patientSearchResults: mapped,
                  isSearchingPatients: false,
                })
              },
              error: (err) => {
                console.error('[Store] Patient search failed', err)
                patchState(store, { isSearchingPatients: false })
              },
            }),
          ),
        ),

        init() {
          const userId = storageService.getUserId()
          if (!userId) return

          loadConversations({ userId: Number(userId) })

          mercureService.init()
          mercureService.subscribe(`/users/${userId}/conversations`)

          mercureService.messages$.subscribe(({ topic, data }) => {
            const rawData = data as MercureEventData
            const status = rawData.status
            const topicConvId = topic.startsWith('/conversations/')
              ? Number(topic.split('/').pop())
              : null
            const convId = topicConvId || Number(rawData.id)

            // If it's a conversation topic, 'id' in data is likely the message ID if 'messageId' is missing
            const messageId = Number(
              rawData.messageId || (topicConvId ? rawData.id : null),
            )

            if (!convId) return

            // 1. Handle Typing
            if (status === 'typing') {
              // Ignore if it comes from self
              const fromId = rawData.from
              if (fromId && String(fromId) === String(userId)) {
                return
              }
              setConversationTyping(convId, true)
              return
            }

            // 2. Handle New Messages
            if (
              status === 'new' ||
              !['read', 'doc_opened'].includes(status || '')
            ) {
              if (topic === `/users/${userId}/conversations`) {
                loadConversations({ userId: Number(userId) })

                // If active convo, refresh
                if (convId === store.selectedConversationId()) {
                  this.silentlyRefreshMessages(convId, Number(userId))
                  this.markAsRead(convId)
                }
              } else if (topic.startsWith('/conversations/')) {
                this.silentlyRefreshMessages(convId, Number(userId))
              }
              return
            }

            // 3. Handle Status Updates (read, doc_opened)
            if (status === 'read' || status === 'doc_opened') {
              if (!messageId) return

              patchState(store, (state) => {
                const updateMsg = (m: IMessage) => {
                  if (status === 'read') {
                    // Cumulative read: mark as read if it's the target message OR an older one (if chronological)
                    // Since we map ID from messageId, if we have a real ID, we can do m.id <= messageId
                    if (m.id === messageId || (m.id > 0 && m.id <= messageId)) {
                      return { ...m, read: true }
                    }
                  } else if (status === 'doc_opened' && m.id === messageId) {
                    return { ...m, docOpened: true }
                  }
                  return m
                }

                const updatedMessages = state.messages.map(updateMsg)
                const updatedCache = { ...state.messageCache }
                if (updatedCache[convId]) {
                  updatedCache[convId] = updatedCache[convId].map(updateMsg)
                }

                return {
                  messages: updatedMessages,
                  messageCache: updatedCache,
                }
              })
            }
          })
        },
        reset() {
          this.disconnect()
          patchState(store, initialState)
        },
        disconnect() {
          const userId = storageService.getUserId()
          if (userId) {
            mercureService.unsubscribe(`/users/${userId}/conversations`)
          }
        },
      }
    },
  ),
  withHooks({
    onInit(store) {
      store.init()
    },
    onDestroy(store) {
      store.disconnect()
    },
  }),
)
